import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import bgVideo from "../assets/lbg.mp4";

function DocumentModal({ document, onClose }) {
  if (!document) return null;
  const isPdf =
    typeof document.src === "string" &&
    document.src.trim().toLowerCase().startsWith("data:application/pdf");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="relative flex max-h-[88vh] w-full max-w-5xl flex-col rounded-lg border border-white/10 bg-slate-950 p-4 shadow-2xl">
        <div className="mb-3 flex items-center justify-between border-b border-white/10 pb-3">
          <h3 className="text-lg font-bold text-white">{document.label}</h3>
          <button
            onClick={onClose}
            className="rounded-lg border border-white/15 px-3 py-1 text-sm font-semibold text-slate-200 transition hover:bg-white/10"
          >
            X
          </button>
        </div>
        {isPdf ? (
          <iframe
            src={document.src}
            title={document.label}
            className="min-h-0 w-full flex-1 rounded bg-white"
          />
        ) : (
          <img
            src={document.src}
            alt={document.label}
            className="min-h-0 w-full flex-1 rounded object-contain"
          />
        )}
      </div>
    </div>
  );
}

function TenantProfile() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDocument, setSelectedDocument] = useState(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await api.get("/profile");
      setUser(res.data);
      if (res.data?.name) {
        localStorage.setItem("username", res.data.name);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/");
        return;
      }
      setError("Unable to load your profile.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const documents = user
    ? [
        { label: "Barangay Clearance", src: user.barangay_clearance },
        { label: "Valid ID", src: user.valid_id },
        { label: "Proof of Income", src: user.proof_of_income || user.contract },
      ].filter((doc) => doc.src)
    : [];

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans">
      <video data-workspace-background="true" aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover"
        src={bgVideo}
        autoPlay
        muted
        loop
        playsInline
      />
      <div className="absolute inset-0 bg-slate-950/86"></div>
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80"></div>

      <DocumentModal
        document={selectedDocument}
        onClose={() => setSelectedDocument(null)}
      />

      <div className="workspace-page-body relative z-10 min-h-screen px-4 py-16 text-white sm:px-6 md:py-10 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 border-b border-white/10 pb-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.35em] text-teal-300">
              Tenant Account
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-wide md:text-4xl">
              My Profile
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300 md:text-base">
              View your account details and uploaded tenant documents.
            </p>
          </div>

          
        </div>

        {loading ? (
          <div className="rounded-lg border border-white/10 bg-slate-950/90 py-10 text-center text-slate-300">
            Loading profile...
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-300/30 bg-red-500/15 py-10 text-center text-red-100">
            {error}
          </div>
        ) : (
          <div className="grid gap-6 xl:grid-cols-[minmax(0,0.85fr)_minmax(420px,1fr)]">
            <section className="rounded-lg border border-white/10 bg-slate-950/90 p-5 shadow-2xl">
              <div className="mb-5 border-b border-white/10 pb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                  Personal Information
                </p>
                <h2 className="mt-2 text-2xl font-bold text-white">
                  {user?.name || "Tenant"}
                </h2>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                    Email
                  </p>
                  <p className="mt-1 break-words font-semibold text-white">
                    {user?.email || "Not provided"}
                  </p>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/5 p-4">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                    Contact
                  </p>
                  <p className="mt-1 font-semibold text-white">
                    {user?.contact_number || "Not provided"}
                  </p>
                </div>
                <div className="rounded-lg border border-white/10 bg-white/5 p-4 sm:col-span-2">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-400">
                    Address
                  </p>
                  <p className="mt-1 font-semibold text-white">
                    {user?.address || "Not provided"}
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-lg border border-white/10 bg-slate-950/90 p-5 shadow-2xl">
              <div className="mb-5 border-b border-white/10 pb-4">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                  Uploaded Documents
                </p>
                <h2 className="mt-2 text-2xl font-bold text-white">
                  Tenant IDs
                </h2>
              </div>

              {documents.length > 0 ? (
                <div className="grid gap-3">
                  {documents.map((doc) => (
                    <button
                      key={doc.label}
                      onClick={() => setSelectedDocument(doc)}
                      className="rounded-lg border border-white/10 bg-white/5 p-4 text-left transition hover:border-teal-300/50 hover:bg-teal-500/10"
                    >
                      <p className="font-semibold text-white">{doc.label}</p>
                      <p className="mt-1 text-sm text-slate-400">
                        Click to view uploaded document.
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-white/15 px-4 py-8 text-center text-sm text-slate-300">
                  No uploaded documents found.
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

export default TenantProfile;
